import authService from '@/api/authService';
import { sleep } from 'common/misc/sleep';
import { Bucket } from 'common/types/bucket';
import { BucketDeleteMessage } from 'common/types/bucketDeleteMessage';
import { BucketUpdateId } from 'common/types/bucketUpdateId';
import { MaintenanceState } from 'common/types/maintenance';
import {
    calcDuration,
    createCache,
    type Cache,
    type CacheFunction,
    type CacheOptions,
    type CreateCacheResult,
    type Duration,
} from 'cross-state';
import { io } from 'socket.io-client';
import type z from 'zod';

export const socket = io({
    // Auth is carried by the better-auth session cookie (same origin); pass impersonation /
    // admin-role elevation alongside it.
    withCredentials: true,
    autoConnect: false,
});

authService.appLogin.state
    .map((state) => !!state.value)
    .subscribe((isLoggedIn) => {
        if (isLoggedIn) {
            socket.connect();
        } else {
            socket.disconnect();
        }
    });

const firstConnection = new Promise<void>((resolve) => {
    socket.once('connect', resolve);
});

const socketConnectionInitialized = Promise.race([firstConnection, sleep(2000)]);

const topics = {
    bucketUpdate: Bucket,
    bucketDelete: BucketDeleteMessage,
    maintenance: MaintenanceState,
};

type Handlers<T> = {
    [K in keyof typeof topics as `on${Capitalize<string & K>}`]?: (
        data: z.infer<(typeof topics)[K]>,
        cache: T,
    ) => void;
};

export function createSocketCache<T, Args extends any[]>(
    {
        fetch,
        ...handlers
    }: {
        fetch: CacheFunction<T, Args>;
    } & Handlers<CreateCacheResult<T, Args, Cache<T, Args>>>,
    options?: CacheOptions<T, Args>,
) {
    const cache = createCache<T, Args>(
        (async (...args) => {
            await socketConnectionInitialized;
            return fetch(...args);
        }) as CacheFunction<T, Args>,
        {
            invalidateOnWindowFocus: false,
            ...options,
        },
    );

    for (const topic in topics) {
        const handlerName =
            `on${topic.charAt(0).toUpperCase()}${topic.slice(1)}` as keyof typeof handlers;
        const handler = handlers[handlerName];
        if (!handler) continue;

        onSocketMessage(topic as keyof typeof topics, (data) => handler(data as any, cache));
    }

    socketConnectionInitialized.then(() => {
        socket.on('connect', () => {
            cache.invalidateAll();
        });
    });

    return cache;
}

export function onSocketMessage<T extends keyof typeof topics>(
    topic: T,
    handler: (data: z.infer<(typeof topics)[T]>) => void,
) {
    const onMessage = (data: unknown) => {
        const parsedData = (topics as any)[topic].parse(data);
        handler(parsedData);
    };
    socket.on(topic as string, onMessage);
    return () => {
        socket.off(topic as string, onMessage);
    };
}

export async function waitForUpdate(action: () => Promise<unknown>, timeout: Duration = 10_000) {
    const d = new DisposableStack();
    const seenVersions = new Map<string, number>();
    let awaitedId: string | undefined;
    let awaitedVersion: number | undefined;

    const messagePromise = new Promise<void>((resolve) => {
        d.defer(
            onSocketMessage('bucketUpdate', (bucket) => {
                seenVersions.set(bucket.id, bucket._v ?? 0);

                if (
                    typeof awaitedId === 'string' &&
                    typeof awaitedVersion === 'number' &&
                    bucket.id === awaitedId &&
                    (bucket._v ?? 0) >= awaitedVersion
                ) {
                    d.dispose();
                }
            }),
        );

        d.defer(resolve);
    });

    const timeoutId = setTimeout(() => d.dispose(), calcDuration(timeout));
    d.defer(() => clearTimeout(timeoutId));

    try {
        const result = BucketUpdateId.parse(await action());
        awaitedId = result.id;
        awaitedVersion = result._v;

        const lastSeenVersion = seenVersions.get(awaitedId);
        if (typeof lastSeenVersion === 'number' && lastSeenVersion >= awaitedVersion) {
            d.dispose();
        }

        await messagePromise;
    } finally {
        // A no-op after a match or timeout (the stack is already disposed). When the action
        // rejects or its result does not parse, this is what unsubscribes the listener and
        // clears the timeout.
        d.dispose();
    }
}

export async function waitForDelete(
    bucketId: string,
    action?: () => unknown,
    timeout: Duration = 10_000,
) {
    const d = new DisposableStack();

    const messagePromise = new Promise<void>((resolve) => {
        d.defer(
            onSocketMessage('bucketDelete', (message) => {
                if (message.bucketId !== bucketId) return;
                d.dispose();
            }),
        );

        d.defer(resolve);
    });

    const timeoutId = setTimeout(() => d.dispose(), calcDuration(timeout));
    d.defer(() => clearTimeout(timeoutId));

    try {
        await action?.();
        await messagePromise;
    } finally {
        // see waitForUpdate
        d.dispose();
    }
}
